// 중첩 반복의 **분할 경계**와 조각 continuation 형상(순수, DOM/React 비의존).
//
// 한 반복 항목이 페이지에 안 들어가 안쪽 반복 항목 경계에서 쪼개질 때, 항목 안의 **비반복 내용
// (사진·설명 등 = lead)은 첫 조각에만** 나온다. 사진을 조각마다 되풀이하는 안은 사용자가 기각했다
// (결정 2026-08-18 대안 D — "내용 중복"). 그래서 continuation 낱장에서는 항목 template을
// **안쪽 반복으로 가는 체인만 남기고** 렌더한다.
//
// ⚠️ 이 규칙은 **측정(pageMeasure)과 렌더(RenderTemplate)가 반드시 같아야** 한다 — 계획은 측정이
// 낸 높이로 낱장을 자르고 렌더는 이 형상을 그리므로, 둘이 갈리면 계획이 틀린 높이로 자른다.
// 그래서 규칙을 이 한 파일에 두고 양쪽이 같은 함수를 부른다(두 번째 통로를 만들지 않는다).

import type { ComposerNode, GroupNode, RepeatNode, TableNode } from '@safetyhub/shared';

/** 분할 경계가 될 수 있는 노드 — 안쪽 repeat(단위=항목) 또는 안쪽 table(단위=행). */
export type NestedBoundaryNode = RepeatNode | TableNode;

/**
 * 항목 template 안의 **유일한** 분할 경계 노드. 없거나 후보가 여럿이면 null.
 *
 * 경계 우선순위(결정 2026-08-18)는 **1순위 안쪽 repeat 항목 → 3순위 표 행**이다. 그래서 판정도
 * 두 단계다:
 *   ① repeat이 정확히 1개면 그게 경계다(표가 함께 있어도 repeat이 이긴다 — 종전 판정 그대로).
 *   ② repeat이 하나도 없고 table이 정확히 1개면 그 표가 경계다(단위 = 행).
 * 그 외(repeat 2개+ / repeat 0 + table 2개+ / 아무것도 없음)는 null.
 *
 * 여럿을 배제하는 이유: 조각 형상(무엇을 남기고 무엇을 lead로 볼지)과 고정분 분해가 모호해진다.
 * 경계를 안 주면 그 항목은 종전대로 통짜/넘침이라 안전하게 degrade한다.
 * 경계 노드 자신의 하위로는 내려가지 않는다 — repeat이면 그 층의 재귀 몫이고(깊이 무관), 표 셀 안의
 * repeat은 페이지 흐름 경계가 아니다(그 층의 어휘는 행 분할).
 *
 * **개정(결정 2026-09-03 심야)**: "여럿이면 degrade"는 더 이상 최종 규칙이 아니다 — 이 함수가
 * null을 내면 `unitFlowChunks`의 **순차 다경계**(문서 순서가 모호성을 해소: lead 구간과 경계들을
 * 문서 순으로 낱장에 채우며 각 경계에서 자름)가 이어받는다. 이 함수 자체는 종전 판정 그대로 둔다 —
 * 경계 1개(또는 0개 + 표 1개) 형상의 lead/continuation 조각 모델이 바이트 동일해야 하기 때문(회귀 0).
 */
export function findSoleNestedBoundary(template: GroupNode): NestedBoundaryNode | null {
  const repeats: RepeatNode[] = [];
  const tables: TableNode[] = [];
  const walk = (node: ComposerNode): void => {
    if (node.kind === 'repeat') {
      repeats.push(node);
      return;
    }
    if (node.kind === 'table') {
      tables.push(node);
      return;
    }
    if (node.kind === 'group' || node.kind === 'stack') {
      for (const c of node.children) walk(c);
    } else if (node.kind === 'branch') {
      for (const slot of node.branches) {
        for (const c of slot.children) walk(c);
      }
    }
  };
  for (const c of template.children) walk(c);
  if (repeats.length === 1) return repeats[0];
  if (repeats.length === 0 && tables.length === 1) return tables[0];
  return null;
}

/**
 * 항목 template 안의 **유일한 채움 표**(`layout.pageFill` 표) — 반복 단위 안 낱장 채움의 대상
 * (결정 2026-09-03).
 *
 * **둘 이상이면 null = 전부 무시(채움 생략, 종전 무동작 그대로)** — "첫 표"를 고르지 않는다.
 * 잔여는 하나의 표만 흡수할 수 있는데 순서 기반 선택은 저작 순서 변경만으로 채움 대상이 조용히
 * 바뀌는 함정이고, 절반만 동작하는 상태는 저작 실수를 은폐한다. 경계 유일성 판정
 * (findSoleNestedBoundary "여럿이면 degrade")과 같은 규약이다. 0개도 null(신호 없음).
 *
 * `findSoleNestedBoundary`와 **별개의 축**이다 — 경계(분할 지점)는 유일한 repeat/표지만, 채움 표는
 * 경계가 아니어도 된다(실사용: TBM 회의록 단위의 경계는 교육기록 repeat이고 참석자 채움 표는 lead
 * 소속). walk 규약은 경계 탐색과 동일 — repeat 하위로는 내려가지 않고(그 층의 몫), 표 셀 안은
 * 페이지 흐름이 아니다.
 */
export function findSoleUnitFillTable(template: GroupNode): TableNode | null {
  const fills: TableNode[] = [];
  const walk = (node: ComposerNode): void => {
    if (node.kind === 'repeat') return; // 안쪽 반복의 채움은 그 층의 몫
    if (node.kind === 'table') {
      if (node.layout?.pageFill === true) fills.push(node);
      return;
    }
    if (node.kind === 'group' || node.kind === 'stack') {
      for (const c of node.children) walk(c);
    } else if (node.kind === 'branch') {
      for (const slot of node.branches) {
        for (const c of slot.children) walk(c);
      }
    }
  };
  for (const c of template.children) walk(c);
  return fills.length === 1 ? fills[0] : null;
}

// ── 순차 다경계(결정 2026-09-03 심야): 단위 안 "mini-body" 청크열 ─────────────
// 경계 후보가 여럿이라 findSoleNestedBoundary가 null인 단위(예: TBM 부 = 회의록 표들 +
// photosRepeat + eduEntriesRepeat + 특이사항)를, 최상위 body와 같은 규약의 **흐름 청크열**로
// 분해한다: 구간(비분할 노드)과 경계(table/repeat)가 문서 순서로 낱장을 채우며 각 경계에서
// 잘린다. 측정·계획·렌더가 **같은 함수**로 같은 청크열을 봐야 인덱스가 어긋나지 않으므로
// 분해를 이 한 파일에 둔다(findSoleNestedBoundary와 같은 이유).

/**
 * branch를 청크 평탄화에서 **투명 통과**시킬 가치가 있는가 — 어느 슬롯이든 직속 자식(중첩 branch
 * 투명 통과 포함)에 table/repeat이 있으면 참. 최상위 `slotGainsFromDescent`(branchPagination)와
 * 같은 판단이되, 측정 시점에 활성 슬롯을 모르는 단위 안에서는 **전 슬롯**을 본다(비활성 슬롯
 * 청크는 DOM에 없어 높이 0 — 계획에 무해). 이득 없는 branch는 원자 청크로 남긴다(제목만 든
 * branch를 쪼개 orphan을 만들지 않는다 — 최상위와 같은 보수 규약).
 *
 * export 이유(2026-09-05): admin 에디터 게이트(isPageFlowPosition)가 "이득 branch 노드 자체도
 * 흐름 위치"(pageBreak가 슬롯 첫 청크로 전파돼 소비됨 — unitFlowChunks 주석)를 판정할 때 이
 * **한 함수**를 공유해야 한다 — 갈리면 에디터가 무동작 함정/과잉 차단을 만든다(chunkFlowActive
 * 공유와 같은 원칙).
 */
export function branchGainsChunks(node: ComposerNode & { kind: 'branch' }): boolean {
  return node.branches.some((slot) =>
    slot.children.some(
      (c) =>
        c.kind === 'table' ||
        c.kind === 'repeat' ||
        (c.kind === 'branch' && branchGainsChunks(c)),
    ),
  );
}

/**
 * 단위 template의 **흐름 청크열**(문서 순서) — template 직속 자식 + 이득 있는 branch의 전 슬롯
 * 자식(투명 통과). group/stack은 **원자 청크**다(최상위 계획이 group을 통짜로 다루는 것과 동형 —
 * 그 안의 table/repeat은 경계로 살아나지 않는다). 경계 청크 = kind가 table/repeat인 청크.
 *
 * **branch pageBreak 전파(2026-09-05 일원화)**: 이득 branch는 평탄화로 청크열에서 사라지므로
 * branch 자체의 `pageBreak:'before'`는 이대로면 영영 무소비다(저작이 슬롯마다 첫 자식에 이중
 * 선언해야 했던 함정 — 한 슬롯의 선언만 지우면 다른 슬롯 것이 남는데 에디터는 슬롯을 탭으로
 * 하나씩만 보여줘 안 보인다). 그래서 평탄화가 선언을 **각 슬롯의 첫 방출 청크**에 클론
 * (`{...n, pageBreak:'before'}` — id 유지라 렌더 prune·계획 채널 무영향)으로 전파한다. 슬롯 첫
 * 자식이 또 이득 branch(중첩 투명 통과)면 그 안 각 슬롯의 첫 청크까지 재귀 전파. 첫 청크에
 * 이미 자체 선언이 있으면 그대로 둔다(같은 값이라 무해). 이 한 곳으로 측정(pageMeasure의
 * `cn.pageBreak`)·unitHasBreakDecl·chunkFlowActive가 자동 정합한다(두 번째 통로 없음).
 *
 * ⚠️ 한계(의도): 전파는 **구조상 첫 청크**에 붙는다 — 그 청크가 렌더에서 h=0(0항목 repeat 등)
 * 이어도 다음 청크로 이월하지 **않는다**. 이월하면 "비활성 슬롯 청크는 h=0이라 계획 게이트
 * (h>0)가 걸러 무해"라는 필터와 충돌해 다른 슬롯 청크로 break가 샌다. h=0 첫 청크 단위는
 * break 미발동으로 degrade한다(선언 무소비 — 저작이 첫 청크를 gap 등 높이 있는 노드로 두면 해소).
 *
 * 렌더(RepeatItem 청크 창 prune)·측정(청크별 인스턴스 측정)·계획(chunkRange 인덱스)이 전부
 * 이 배열의 **인덱스**로 대화하므로, 세 층이 반드시 이 함수를 공유해야 한다.
 */
export function unitFlowChunks(template: GroupNode): ComposerNode[] {
  const out: ComposerNode[] = [];
  const push = (nodes: ComposerNode[], pendingBreak: boolean): void => {
    nodes.forEach((n, i) => {
      // 전파 대상은 각 목록의 **첫 노드**뿐 — 상위(branch 선언 또는 그 재귀)의 pending이 여기서 소진된다.
      const inherit = pendingBreak && i === 0;
      if (n.kind === 'branch' && branchGainsChunks(n)) {
        const slotPending = inherit || n.pageBreak === 'before';
        for (const slot of n.branches) push(slot.children, slotPending);
      } else if (inherit && n.pageBreak !== 'before') {
        out.push({ ...n, pageBreak: 'before' });
      } else {
        out.push(n);
      }
    });
  };
  push(template.children, false);
  return out;
}

/**
 * 청크열이 **청크 모드**를 발동시키는가 — 분할 가능 청크(table/repeat) **또는 pageBreak:'before'
 * 선언 청크**(2026-09-05 확장: 선언 소비는 fit·분할 가능성과 무관한 무조건 축이라, 분할 청크가
 * 없어도 청크 모드가 서야 선언이 소비된다)가 있으면 참.
 *
 * 측정(pageMeasure의 chunkMode)과 에디터 게이트(admin isPageFlowPosition)가 이 **한 함수**를
 * 공유한다 — 갈리면 에디터가 무동작 함정(허용했는데 엔진이 안 읽음) 또는 과잉 차단을 만든다.
 * template 구조만 보는 순수 판정이라 전 단위·전 측정 회차에 동일하다.
 */
export function chunkFlowActive(chunks: readonly ComposerNode[]): boolean {
  return chunks.some(
    (c) => c.kind === 'table' || c.kind === 'repeat' || c.pageBreak === 'before',
  );
}

/**
 * 단위 template의 청크열에 pageBreak:'before' **선언** 청크가 존재하는가(2026-09-05) — 순수
 * template 판정(측정·클립·렌더 상태 무관, 전 단위·전 회차 동일).
 *
 * 이 선언이 있으면 단위는 **유일 경계(findSoleNestedBoundary) 형상이어도 청크 경로로 승격**된다 —
 * lead/continuation 모델에는 break 어휘가 없어, 승격하지 않으면 선언이 영영 무소비다(실사용:
 * TBM 다회차 세션 부 = 교육기록 repeat 1개 + 「교육 현장 기록부터 새 페이지」 선언). 측정
 * (pageMeasure의 innerNode 게이트)과 렌더(RepeatItem의 innerId 게이트)가 이 **한 함수**를
 * 공유한다 — 갈리면 계획이 틀린 높이로 자른다(파일 상단 규약). 선언 없는 단위는 종전 유일 경계
 * 판정 그대로다(회귀 0 — lead/continuation 형상 바이트 동일).
 * 렌더 경로에서 항목마다 트리를 다시 걷지 않도록 template 참조 기준 캐시(soleNestedBoundaryId 동형).
 */
const unitBreakDeclCache = new WeakMap<GroupNode, boolean>();
export function unitHasBreakDecl(template: GroupNode): boolean {
  const hit = unitBreakDeclCache.get(template);
  if (hit !== undefined) return hit;
  const found = unitFlowChunks(template).some((c) => c.pageBreak === 'before');
  unitBreakDeclCache.set(template, found);
  return found;
}

/**
 * 청크 창 조각의 항목 형상 — template에서 **`visible`에 든 청크(서브트리 통짜)와 그리로 가는
 * branch 래퍼만** 남긴 사본. `pruneToNestedBoundary`(체인 prune)의 청크판이다.
 *
 * branch 래퍼는 슬롯 구조(조건·개수)를 유지한 채 각 슬롯 자식만 좁힌다 — 활성 슬롯 판정이 첫
 * 조각과 동일해야 같은 자리에 같은 것이 남는다. 살아남은 슬롯 자식이 하나도 없는 branch는
 * 통째로 떨어진다(투명 래퍼라 출력 영향 0 — 없는 컨테이너의 chrome은 계획이 보수적으로
 * 과충전할 뿐 넘치지 않는다, pageMeasure의 단위 chrome 주석 참조).
 */
export function pruneToChunkWindow(template: GroupNode, visible: ReadonlySet<string>): GroupNode {
  const keep = (nodes: ComposerNode[]): ComposerNode[] => {
    const out: ComposerNode[] = [];
    for (const n of nodes) {
      if (n.kind === 'branch' && branchGainsChunks(n)) {
        const branches = n.branches.map((slot) => ({ ...slot, children: keep(slot.children) }));
        if (branches.some((slot) => slot.children.length > 0)) out.push({ ...n, branches });
      } else if (visible.has(n.id)) {
        out.push(n);
      }
    }
    return out;
  };
  return { ...template, children: keep(template.children) };
}

/**
 * continuation 조각의 항목 형상 — template에서 **`boundaryId`(경계 repeat 또는 표)로 가는 체인만**
 * 남긴 사본.
 *
 * lead 식별 규칙 = "체인 밖 전부". 경계 노드의 **앞 형제**만이 아니라 뒤 형제도, 중간 group 안의
 * 형제도 함께 사라진다(사진이 반복 뒤에 오는 저작도 같은 규칙으로 처리된다). 체인 위의 컨테이너
 * (group/stack/branch)는 **남긴다** — 폭 컨텍스트와 박스 장식이 첫 조각과 같아야 안쪽 항목이
 * 같은 폭으로 접혀 높이가 조각마다 달라지지 않는다(측정 진동 방지).
 *
 * ⚠️ 체인 컨테이너를 남기면 그 padding/border는 **continuation 조각에도 남는다**(= contHeight).
 * 계획식은 그 값을 0으로 가정하지 않고 측정해서 쓴다 — `measureNestedBoundary` 주석 참조.
 *
 * 남길 게 없으면 null(호출측이 원본 template을 그대로 쓴다 = 종전 동작).
 */
export function pruneToNestedBoundary(template: GroupNode, boundaryId: string): GroupNode | null {
  // 상호 재귀라 function 선언(호이스팅)으로 둔다 — const 화살표면 서로를 정의 전에 참조하게 된다.
  function prune(node: ComposerNode): ComposerNode | null {
    // 경계 노드(repeat 또는 table)는 대상일 때만 남는다. 대상이 아닌 경계 후보는 lead 취급이라
    // 체인이 될 수 없다(체인은 유일한 경계까지만 이어진다 — findSoleNestedBoundary가 그 유일성을 보증).
    if (node.kind === 'repeat' || node.kind === 'table') {
      return node.id === boundaryId ? node : null;
    }
    if (node.kind === 'group') {
      const children = keep(node.children);
      return children.length > 0 ? { ...node, children } : null;
    }
    if (node.kind === 'stack') {
      const children = keep(node.children);
      return children.length > 0 ? { ...node, children } : null;
    }
    if (node.kind === 'branch') {
      // 슬롯 구조(조건·개수)는 유지하고 각 슬롯의 children만 체인으로 좁힌다 — 활성 슬롯 판정이
      // 첫 조각과 동일해야 같은 자리에 같은 것이 남는다.
      const branches = node.branches.map((slot) => ({ ...slot, children: keep(slot.children) }));
      return branches.some((slot) => slot.children.length > 0) ? { ...node, branches } : null;
    }
    return null; // 체인 밖 잎 노드(사진·텍스트·표·스페이서 등) = lead → 조각에서 제거
  }
  function keep(nodes: ComposerNode[]): ComposerNode[] {
    const out: ComposerNode[] = [];
    for (const n of nodes) {
      const p = prune(n);
      if (p !== null) out.push(p);
    }
    return out;
  }
  const children = keep(template.children);
  return children.length > 0 ? { ...template, children } : null;
}
